import { type ReactNode, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/**
 * PLAYER LISTS ON A PHONE — owner, 2026-10-16, option A of two designs he was shown: "a list, tap for
 * details", the way Contacts, Linear and Stripe work on a phone. A table scrolled sideways and hid
 * its last columns; a grid of labels and values read as "random words everywhere". So below `sm`
 * every player list is short rows — initials, name, plain phrases that name what they are ("Steady
 * pace · Subbed 3 times") — and tapping a row opens a panel from the bottom with the full details,
 * the labelled values and the actions. On a computer the same lists stay tables.
 */

export function MobileList({ children }: { children: ReactNode }) {
  return <ul className="overflow-hidden rounded-[14px] border border-light bg-paper sm:hidden">{children}</ul>;
}

export function Initials({ firstName, lastName, onTint = false }: { firstName: string; lastName: string; onTint?: boolean }) {
  const letters = `${firstName.trim()[0] ?? ''}${lastName.trim()[0] ?? ''}`.toUpperCase();
  return (
    <span aria-hidden="true" className={`flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full text-[15px] font-semibold text-deep ${onTint ? 'bg-paper' : 'bg-tint-deep'}`}>
      {letters}
    </span>
  );
}

/**
 * One row: tapping opens its panel (`onOpen`) or, with `to`, goes to the player's page. With `select`
 * the list is in Select mode, as Mail and Photos do it: a round tick box leads the row and tapping
 * anywhere on it ticks it.
 */
export function MobileRow({ firstName, lastName, lines, onOpen, to, select }: {
  firstName: string;
  lastName: string;
  lines: ReactNode[];
  onOpen?: () => void;
  to?: string;
  select?: { checked: boolean; onChange: (checked: boolean) => void } | undefined;
}) {
  const body = (
    <>
      {select ? (
        <span aria-hidden="true" className={[
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[13px] font-bold',
          select.checked ? 'border-deep bg-deep text-on-deep' : 'border-stroke bg-paper text-transparent',
        ].join(' ')}>✓</span>
      ) : null}
      <Initials firstName={firstName} lastName={lastName} onTint={Boolean(select?.checked)} />
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-semibold text-ink">{firstName} {lastName}</span>
        {lines.filter(Boolean).map((line, index) => (
          <span key={index} className="mt-0.5 block text-[14px] text-ink-2">{line}</span>
        ))}
      </span>
      {select ? null : <span aria-hidden="true" className="text-[22px] text-ink-3">›</span>}
    </>
  );
  const row = 'flex w-full items-center gap-3 px-3.5 py-3.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-deep';
  return (
    <li className={['border-b border-light last:border-b-0', select?.checked ? 'bg-tint-deep' : ''].join(' ')}>
      {select ? (
        <label className={`${row} cursor-pointer`}>
          <input type="checkbox" className="sr-only" checked={select.checked} onChange={(event) => select.onChange(event.target.checked)} aria-label={`Select ${firstName} ${lastName}`} />
          {body}
        </label>
      ) : to ? <Link to={to} className={row}>{body}</Link> : <button type="button" className={row} onClick={onOpen}>{body}</button>}
    </li>
  );
}

/**
 * The panel from the bottom. A native <dialog> opened with showModal(), like every confirmation on
 * the platform: the page behind goes inert, focus stays inside, Escape and a tap outside close it,
 * and focus returns to the row that opened it.
 */
export function Sheet({ open, onClose, title, subtitle, children, footer }: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer: ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);
  return (
    <dialog
      ref={dialog}
      aria-label={title}
      onClose={onClose}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
      /* From the bottom on a phone; a centred box on a computer, where Set pace opens it too. */
      className="m-0 mt-auto max-h-[85vh] w-full max-w-none rounded-t-[20px] bg-paper p-0 text-ink backdrop:bg-black/30 sm:m-auto sm:max-w-md sm:rounded-[14px]"
    >
      <div className="px-5 pb-6 pt-2.5">
        <div aria-hidden="true" className="mx-auto mb-3.5 h-[5px] w-10 rounded-full bg-stroke sm:invisible" />
        <h2 className="text-[20px] font-semibold">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-[14px] text-ink-2">{subtitle}</p> : null}
        <div className="mt-4 flex flex-col gap-4">{children}</div>
        <div className="mt-5 flex items-center justify-between gap-3">{footer}</div>
      </div>
    </dialog>
  );
}

/** Two labelled values side by side in the panel: "Pace of play / Steady". */
export function Facts({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-2 gap-2.5">
      {items.map((item) => (
        <div key={item.label} className="rounded-[10px] bg-chalk px-3 py-2.5">
          <dt className="text-[12px] text-ink-3">{item.label}</dt>
          <dd className="mt-0.5 text-[17px] font-medium text-ink">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** "Steady pace", or "Pace not set" — a phrase that says what it is. */
export function pacePhrase(label: string, unknown: boolean): string {
  return unknown ? 'Pace not set' : `${label} pace`;
}

/** "Not subbed yet", "Subbed once", "Subbed 3 times". */
export function subbedPhrase(times: number): string {
  return times === 0 ? 'Not subbed yet' : times === 1 ? 'Subbed once' : `Subbed ${times} times`;
}

/** In the panel, a link that goes to the player's page — underlined, because it goes somewhere. */
export const SHEET_LINK = 'text-[15px] text-ink-2 underline underline-offset-4 hover:text-ink';
/** In the panel, the action that takes something away. */
export const SHEET_DANGER = 'min-h-11 rounded-md px-1 text-[15px] text-rust hover:bg-tint-rust';
export const SHEET_DONE =
  'inline-flex h-11 items-center rounded-md border border-control-border bg-paper px-5 text-[15px] font-medium text-ink hover:bg-sand';
